/**
 * THE PLACE TABLE. TWENTY PLACES, TWENTY NAMES, FIVE AREAS. NO PLACEHOLDERS LEFT.
 *
 * -- WHAT CHANGED, AND THE CLAIM THIS FILE USED TO MAKE ----------------------
 *
 * This header has now been wrong twice and both corrections are kept, because the second one
 * only makes sense against the first.
 *
 * It once said `world-places` and `story` were "both blocked on the owner's outstanding ruling
 * on the fiction". That went false when the owner ruled, and was corrected.
 *
 * It then said the other seventeen places "keep their placeholder labels, because a placeholder
 * that says it is a placeholder is honest and seventeen invented names would not be". THAT IS
 * NOW FALSE TOO, and CONTRACT 1.5m is why:
 *
 *   1.5m(c)  "The size of the islands and what is on them and the type of climate/environment
 *            should be different in different areas."
 *   1.5m(d)  Island identity is TWO LEVELS. The AREA supplies climate, palette family, building
 *            vernacular, what grows there and what people do. The ISLAND supplies its own name,
 *            flag, silhouette and life within that.
 *   1.5m     staging note: the next deliverable is "a few examples of completed islands for me
 *            to judge", diverging BY REGION. That supersedes 1.5h(c)'s one-at-a-time staging for
 *            this step, in the owner's own words, given in the same breath as the ruling.
 *
 * A name is not the same commitment as a realised island. What 1.5h(c) stages one at a time is
 * the ART - the 51 placements, the palette, the parts. That staging is NOT abandoned here and
 * the fence for it moved rather than went away: `src/render/islets.ts` `spriteForPlace` is what
 * says an island has been drawn, exactly one place has an authored sprite, and
 * `src/render/chart.test.ts` now fences THAT instead of fencing the label. Naming twenty places
 * and drawing one is the state 1.5m asks for.
 *
 * WHERE THE IDENTITIES LIVE. `gauntlet/island/REGIONS.md` carries the five areas and all twenty
 * islands: for each, the one trade, what that trade physically needs, what the harbour is for,
 * what the weather demands of a building, and the flag. Murrin Roost's own file
 * (`gauntlet/island/MURRIN-ROOST.md`) is the standard those entries are written to. A name in
 * this file with no entry there is a label, and `areas.test.ts` fails if one appears.
 *
 * -- THE SEAM, WHICH 1.5m DOES NOT MOVE EITHER -------------------------------
 *
 * The only thing the voyage layer DERIVES ANYTHING FROM is `position`. When a place is
 * discovered, where it sits in the progression, what water it is in, WHAT AREA IT IS IN and how
 * long it takes to sail to are all computed from that position, and nothing about them is
 * hand-set. Hand-authored identity and derived progression are not in conflict and they do not
 * even meet: naming a place changes no number below, and the layout solve that positions it
 * never reads the label.
 *
 * The area is the sharp case, so it is fenced rather than asserted. `AREA_BY_PLACE` below is a
 * CLAIM about the derived field, not a source for it: `regions.ts` `areaAt` computes the area
 * from position alone and `areas.test.ts` fails if the claim and the computation disagree. So
 * the writing in REGIONS.md cannot drift away from the geography, and moving an archetype goes
 * red instead of going quiet.
 *
 * A SEED CARRIES ONE MORE FIELD, and as of CONTRACT 1.5n it is read. `extent` says how much water
 * an island takes up AND where that water sits relative to the place point. `calls.ts` solves the
 * boat's arrivals from it and `src/render/islets.ts` draws the island where it says, so the
 * picture and the simulation cannot hold two different opinions about where an island is. It
 * derives nothing about PACING: every sighting distance below is solved before the extent exists.
 *
 * The island's LOOK is still not here. `src/render/islets.ts` holds the place-id-to-sprite map,
 * so the voyage layer never learns that Murrin Roost has a quay and the renderer never learns
 * when it is discovered.
 *
 * -- THE CADENCE, AND WHAT ROUND 1 GOT WRONG ---------------------------------
 *
 * The layout follows the ONE MEASURED CADENCE this run has: Melvor Idle's Fishing unlock
 * levels 1-99 (gauntlet/refs/bar-depth.json), whose largest gap is 10 levels with the
 * last unlock at 95.
 *
 * Round 1 laid places out at those fractions of the TRACK and then measured the cadence
 * over their SIGHTING distances, which are a different set of numbers - a place is
 * sighted up to a radius before the boat reaches it, and its lateral offset moves that
 * again. The measured gap came out at 0.10540 against a 0.10526 bar, and the check
 * passed only because the module widened its own bound by two sighting radii.
 *
 * The bound is no longer widened. Instead the LAYOUT IS SOLVED so that each place's
 * SIGHTING distance - the quantity the player actually experiences, and the one the
 * cadence is a claim about - lands on its cadence fraction of the charted span. The
 * solve is a fixed-count correction on the layout arc length, deterministic and with no
 * convergence test, and its residual is the only tolerance the cadence check allows.
 *
 * The denominator is CADENCE_LAST_UNLOCK_LEVEL (95) rather than the 1-99 span, because
 * the charted sea runs from home to the LAST charted place exactly as Melvor's measured
 * cadence runs from level 1 to its last unlock. Mixing the two would compare our chart
 * against a longer Melvor than the one that was measured.
 */

import { berthOffsetLeagues } from './berth';
import {
  CADENCE_LAST_UNLOCK_LEVEL,
  MELVOR_UNLOCK_LEVELS,
  METRES_PER_LEAGUE,
  SIGHTING_RADIUS_LEAGUES,
  SPIRAL_LEAGUES_PER_RADIAN,
  WORLD_RADIUS_LEAGUES,
} from './constants';
import { PUBLISHED_WORLD_SCALE } from './scale';
import { sightTrackDistanceOf } from './chart';
import { hashLattice } from './noise';
import type { SeaAreaId } from './regions';
import type { PlaceExtent, PlaceSeed } from './seed';
import { spiralArcLength, thetaAtArcLength, trackPositionAtTheta } from './track';
import type { Leagues, Vec2 } from './units';
import { vec2 } from './units';
// Baked DATA, not the renderer: every drawn island's pixel extent, written by
// tools/island/island-coast.mjs. See the PUBLISHED_EXTENTS block below.
import placeExtents from '../render/baked/place-extents.json';

export type { PlaceExtent, PlaceSeed } from './seed';

/** Track distance from home to the outer edge of the charted sea. Leagues. */
export const CHARTED_TRACK_LEAGUES: Leagues = spiralArcLength(WORLD_RADIUS_LEAGUES / SPIRAL_LEAGUES_PER_RADIAN);

/**
 * Fractions of the charted span at which a charted place is SIGHTED. The last unlock
 * sits at exactly 1, so the gaps between consecutive fractions are exactly the measured
 * Melvor level gaps divided by 95 - which is the bar, with nothing added to it.
 */
export const CHARTED_LAYOUT_FRACTIONS: readonly number[] = MELVOR_UNLOCK_LEVELS.map(
  (level) => level / CADENCE_LAST_UNLOCK_LEVEL,
);

/** Correction passes on the layout solve. Fixed count, no convergence test. */
const LAYOUT_SOLVE_PASSES = 24;

function pad2(n: number): string {
  return n < 10 ? `0${n}` : `${n}`;
}

/**
 * THE NAMES, IN CHART ORDER, AND THE AREA EACH ONE IS WRITTEN INTO.
 *
 * Index 0 is `place-01`, the first landfall after home; index 18 is `place-19`, the outermost.
 * Home is `HOME_PLACE_NAME` below and is not in this list, because it is the one island that
 * was named before the sea it sits in was.
 *
 * The `area` field is the AUTHORED CLAIM described in the header. It is checked against
 * `areaAt(position)` by `areas.test.ts` and read by nothing else.
 *
 * NAMES ARE MEASURED AGAINST THE SHEET. The ledger's places leaf clips a row name at
 * `COL_W - 34` = 338 px (`src/render/chart.ts`), and every glyph must exist in `HAND`
 * (`src/render/glyphs.ts`, which has no apostrophe). MEASURED: the widest name here is
 * "Nacre Scaur" at 59 px, 17.5% of the column; Murrin Roost is 62 px; the widest thing the
 * leaf already draws is the species "Yellowfin Tuna" at 72 px. Missing glyphs: none.
 */
interface AuthoredIdentity {
  readonly name: string;
  readonly area: SeaAreaId;
}

const AUTHORED: readonly AuthoredIdentity[] = [
  { name: 'Tangsay', area: 'wrack' }, // 01 - kelp ash for glass and soap
  { name: 'Cloven Rigg', area: 'wrack' }, // 02 - the slate that roofs Murrin Roost
  { name: 'Ederholm', area: 'wrack' }, // 03 - eider down, and a people who never hunt
  { name: 'Larchmouth', area: 'wrack' }, // 04 - the only trees in the Wrack, and a slip
  { name: 'Marrow Bar', area: 'shellbanks' }, // 05 - staked oyster beds you can walk
  { name: 'Nacre Scaur', area: 'shellbanks' }, // 06 - shell cut for buttons and inlay
  { name: 'Alderflet', area: 'shellbanks' }, // 07 - the smokehouses, and a harbour for strangers
  { name: 'Woolstrand', area: 'shellbanks' }, // 08 - sponge divers, roofs used as drying yards
  { name: 'Warden Bar', area: 'shellbanks' }, // 09 - pilots, where the shelf begins
  { name: 'Torsk Vard', area: 'rime' }, // 10 - stockfish racks, the only island in the north
  { name: 'Cleitness', area: 'sunder' }, // 11 - cliff fowling, and boats winched out of the sea
  { name: 'Salina', area: 'glass' }, // 12 - sun salt off a rainless lagoon
  { name: 'Cisterna', area: 'glass' }, // 13 - the spring, and the sea's only fresh water
  { name: 'Kell Skerry', area: 'sunder' }, // 14 - where the chart itself is copied
  { name: 'Blackstage', area: 'sunder' }, // 15 - liver oil, rendered over deep water
  { name: 'Hafdyke', area: 'sunder' }, // 16 - sheep that eat weed, behind a wall round the shore
  { name: 'Pitchgarth', area: 'sunder' }, // 17 - the one careenage in the outer sea
  { name: 'Almadra', area: 'glass' }, // 18 - the tuna trap, worked six weeks a year
  { name: 'Lanterna', area: 'glass' }, // 19 - lamps, night water, and the fish that rise to them
];

/**
 * Lateral offset from the track, so that a place's discovery distance has to be solved
 * for rather than read off the layout. Magnitude stays under the sighting radius, which
 * is what guarantees every charted place is reachable by drift alone.
 */
function lateralOffset(index: number): Leagues {
  const m = 0.3 + 0.4 * hashLattice(index, 7, 0x1a2b3c4d);
  const sign = index % 2 === 0 ? 1 : -1;
  return sign * m * SIGHTING_RADIUS_LEAGUES;
}

/** A position offset perpendicular to the track at layout arc length `s`. */
function positionAtLayoutDistance(s: Leagues, index: number): Vec2 {
  const theta = thetaAtArcLength(Math.max(0, s));
  const base = trackPositionAtTheta(theta);
  const A = SPIRAL_LEAGUES_PER_RADIAN;
  const dx = A * (Math.sin(theta) + theta * Math.cos(theta));
  const dy = A * (Math.cos(theta) - theta * Math.sin(theta));
  const len = Math.hypot(dx, dy) || 1;
  const off = lateralOffset(index);
  return vec2(base.x + (dy / len) * off, base.y + (-dx / len) * off);
}

/**
 * Solve the layout arc length whose SIGHTING distance is `targetSight`.
 *
 * Moving a place one league further along the track moves the distance at which it is
 * sighted by very close to one league, so the residual correction below is a contraction
 * and a fixed 24 passes drive it into the last few bits. There is no convergence test
 * and no early exit: the same target always costs the same work and lands on the same
 * float, on any machine.
 *
 * The label is looked up, never computed, and it is looked up AFTER the position is solved -
 * which is the seam in the header made literal: change a name and this function returns the
 * same float it returned before.
 */
function seedAtSightDistance(index: number, targetSight: Leagues): PlaceSeed {
  let s = targetSight;
  let position = positionAtLayoutDistance(s, index);
  for (let pass = 0; pass < LAYOUT_SOLVE_PASSES; pass += 1) {
    const got = sightTrackDistanceOf(position, SIGHTING_RADIUS_LEAGUES);
    if (!Number.isFinite(got)) break;
    s += targetSight - got;
    position = positionAtLayoutDistance(s, index);
  }
  const authored = AUTHORED[index - 1];
  if (authored === undefined) throw new Error(`no authored identity for place-${pad2(index)}`);
  const id = `place-${pad2(index)}`;
  return {
    id,
    label: authored.name,
    position,
    extent: publishedExtent(id) ?? genericExtent(position),
  };
}

/** Home sits at the origin, discovered from the first second of a new save. */
export const HOME_PLACE_ID = 'place-00';

/**
 * MURRIN ROOST IS HOME, and that is a decision with a reason.
 *
 * The island is a harbour you leave from and bring cloth back to: a stone quay, four boats
 * alongside it, a village in the lee and a hundred and some people who dye, fish for what they
 * eat, and wait for the buyers (gauntlet/island/MURRIN-ROOST.md section 1). The player's boat is
 * the fifth boat at that quay. Putting the one realised island at the origin is also what makes
 * the player SEE it - it is in the first frame of a new save rather than several hours of drift
 * out - and the whole point of that round was that the island existed and nobody had seen it.
 */
export const HOME_PLACE_NAME = 'Murrin Roost';

/** The area home sits in. The Wrack is written around this island, not the other way round. */
const HOME_AREA: SeaAreaId = 'wrack';

/**
 * HOW MUCH SEA A DRAWN ISLAND TAKES UP, READ FROM DATA ITS OWN PIXELS WROTE.
 *
 * Until 2026-09-10 this file carried each large island's geometry by hand: MURRIN_ROOST_PX,
 * TORSK_VARD_PX, a forty-vertex TORSK_COAST_PX array and `index === 10 ? TORSK_VARD_EXTENT`
 * in the layout. CONTRACT 1.5h(b) wants eighteen more islands at 200-400 m, landed one region
 * at a time on another machine, and eighteen hand edits of voyage code is how a sprite and its
 * extent stop agreeing. So the numbers now come from `src/render/baked/place-extents.json`,
 * written by `tools/island/island-coast.mjs` from the baked PNG and `authored.json`, and every
 * drawn island's `PlaceExtent` is built by the ONE function below. Landing an island is: bake
 * the PNG, add it to authored.json, run the tool, commit the JSON. This file needs no edit.
 *
 * -- WHAT THE DATA CARRIES, AND WHAT THIS FUNCTION MAKES OF IT ----------------
 *
 *   spritePx       the full image, so the box is the render/cull extent: half-extents in
 *                  leagues at the PUBLISHED WORLD SCALE (7.8 px/m across, 6.75 up-screen, times
 *                  metres per league). The conversion is the one the constants used and is not
 *                  re-derived here.
 *   anchor         the authored harbour point inside the sprite, which the renderer puts ON the
 *                  place point. So the box centre is (w/2 - anchorX, anchorY - h/2) px from it,
 *                  +y north.
 *   coastNativePx  the padded row-extrema convex hull of the opaque pixels plus the 65 px
 *                  every-heading hull envelope, in sprite pixel coordinates, counter-clockwise in
 *                  world coordinates. Published as `coastLeagues` only when `coastPublished` is
 *                  true (authored.json `navigationCoast`, default true). Murrin sets it false:
 *                  home is called on its box with a departure cubic (`calls.ts`, the origin
 *                  branch) and `murrin-geometry.test.ts` fences those exact figures.
 *
 * MEASURED, and the migration's whole claim: the Murrin and Torsk extents built here are
 * deep-equal to the 4a7aa2e constants to the last bit (`place-extents.test.ts`, which carries
 * those constants as its expected values). The coast vertices and the anchor are integers, so
 * subtracting one from the other before dividing is exact, and the leagues are the same floats
 * the hand-typed array produced.
 *
 * WHAT IT IS FOR is unchanged. The owner reported *"Also, the boat goes right through it"*.
 * `calls.ts` reads the box (or the coast), finds the crossing, and makes the boat FAST AT THE
 * QUAY for the whole of it - CONTRACT 1.5n(a) - so the route the boat is drawn on is clear of the
 * island while the track it is paced by is not. It changes no number in the layout below, and
 * `calls.test.ts` asserts that against a world built without it.
 *
 * A place with no entry in the file keeps `genericExtent` below. The voyage layer imports the
 * JSON and nothing of the renderer; `no-steering.test.ts` admits baked JSON and nothing else.
 */
interface PublishedExtentRecord {
  readonly spritePx: readonly [number, number];
  readonly anchor: readonly [number, number];
  readonly coastPublished: boolean;
  readonly coastNativePx: readonly (readonly [number, number])[];
}

const PX_ACROSS_PER_LEAGUE = PUBLISHED_WORLD_SCALE.pxPerMetreAcross * METRES_PER_LEAGUE;
const PX_UP_PER_LEAGUE = PUBLISHED_WORLD_SCALE.pxPerMetreUp * METRES_PER_LEAGUE;

function isPair(v: unknown): v is readonly [number, number] {
  return Array.isArray(v) && v.length === 2 && v.every((n) => Number.isInteger(n));
}

/** The file, checked for shape once at module load so a malformed bake fails loudly here. */
function readPublishedExtents(raw: unknown): ReadonlyMap<string, PublishedExtentRecord> {
  const places = (raw as { places?: Record<string, unknown> }).places;
  if (places === null || typeof places !== 'object') throw new Error('place-extents.json has no places map');
  const out = new Map<string, PublishedExtentRecord>();
  for (const [id, entry] of Object.entries(places)) {
    const r = entry as Partial<PublishedExtentRecord>;
    if (!isPair(r.spritePx) || !isPair(r.anchor) || typeof r.coastPublished !== 'boolean' ||
      !Array.isArray(r.coastNativePx) || !r.coastNativePx.every(isPair)) {
      throw new Error(`place-extents.json: malformed entry for ${id}`);
    }
    out.set(id, { spritePx: r.spritePx, anchor: r.anchor, coastPublished: r.coastPublished, coastNativePx: r.coastNativePx });
  }
  return out;
}

const PUBLISHED_EXTENTS = readPublishedExtents(placeExtents);

/** Place ids that publish a measured extent, for instruments that need to tell them from generic ones. */
export const PUBLISHED_EXTENT_PLACE_IDS: readonly string[] = [...PUBLISHED_EXTENTS.keys()];

/** THE ONE FUNCTION: a drawn island's world extent from its published pixel record. */
export function extentFromRecord(record: PublishedExtentRecord): PlaceExtent {
  const [width, height] = record.spritePx;
  const [harbourX, harbourY] = record.anchor;
  const box = {
    halfWidthLeagues: width / 2 / PX_ACROSS_PER_LEAGUE,
    halfHeightLeagues: height / 2 / PX_UP_PER_LEAGUE,
    centreOffsetLeagues: vec2(
      (width / 2 - harbourX) / PX_ACROSS_PER_LEAGUE,
      (harbourY - height / 2) / PX_UP_PER_LEAGUE,
    ),
  };
  if (!record.coastPublished) return box;
  // `coastLeagues` first: the key order the 4a7aa2e literal had, so an instrument that
  // serialises the extent (world-readiness.mjs) prints the same bytes it printed before.
  return {
    coastLeagues: record.coastNativePx.map(([x, y]) => vec2(
      (x - harbourX) / PX_ACROSS_PER_LEAGUE,
      (harbourY - y) / PX_UP_PER_LEAGUE,
    )),
    ...box,
  };
}

function publishedExtent(id: string): PlaceExtent | undefined {
  const record = PUBLISHED_EXTENTS.get(id);
  return record === undefined ? undefined : extentFromRecord(record);
}

/**
 * HOW MUCH SEA A PLACE NOBODY HAS DRAWN TAKES UP, and WHERE that water sits relative to the track.
 *
 * MEASURED off the HARBOUR POOL - every generated islet an undrawn place can wear
 * (`src/render/islets.ts` `harbourSpriteIds`). That pool was the two settled sprites and is now
 * six: both settled at 203 x 195 and 203 x 192, both cliff at 187 x 238 and 187 x 187, both spire
 * at 140 x 217 and 140 x 195. The widest is still a settled sprite at 203 px, so the half-width
 * below is unchanged; the tallest is now `cliff-5000` at 238 px, so the half-height went from
 * 2.8167 to 3.4378 leagues, a rise of 0.6211.
 *
 * THE BOX CONTAINS THE BODY, IT DOES NOT MATCH IT, and that was already true before the pool
 * grew - the two settled sprites differ by 3 px of height. Growing the pool makes the box
 * generous by more: a spire is 21 px shorter than the box and a place wearing one is berthed
 * 0.3 leagues further out than its own body needs. That is one-sided in the safe direction. The
 * simulation may believe an island is bigger than it is drawn; it may never believe it is
 * smaller, because that is the belief that puts the boat on a beach.
 * `src/render/islets.test.ts` fails if any sprite in the pool outgrows it.
 *
 * CONTRACT 1.5m(b) SAYS SIZE VARIES BY AREA, AND THIS BOX IS STILL NOT THAT. The drawn islands
 * now vary - a spire is 140 px across and a settled plateau 203 - but the box they are berthed by
 * is the same for all of them, because it has to hold whichever one the deal gives a place and
 * the voyage layer is not told which. The sizes each area calls for are written in
 * `gauntlet/island/REGIONS.md` and are a claim on the art rounds, not on this constant: the day
 * an area's islands are drawn at their own size, this becomes a per-area lookup and
 * `islets.test.ts` is what will notice. Saying so here is cheaper than letting a future reader
 * mistake a containing box for a ruled size.
 *
 * THE OFFSET IS THE WHOLE POINT OF THIS ROUND. It used to be a flat 4.59 leagues due NORTH, in
 * `src/render/islets.ts`, decided by the sprite's own foot and by nothing about the track - and
 * MEASURED against the shipped layout it moved ELEVEN of the twenty islands TOWARD the track and
 * left one 0.419 leagues from it. CONTRACT 1.5n(b) rules that placement stops being decoration
 * applied after the track exists, so the offset now comes from `berth.ts`, which knows where the
 * coils are, and the RENDERER READS IT FROM HERE rather than deriving its own. One rule, two
 * readers, one fence.
 */
export const GENERIC_PLACE_HALF_EXTENT = {
  halfWidthLeagues: 203 / 2 / 40,
  halfHeightLeagues: 238 / 2 / 34.615,
} as const;

function genericExtent(position: Vec2): PlaceExtent {
  return {
    halfWidthLeagues: GENERIC_PLACE_HALF_EXTENT.halfWidthLeagues,
    halfHeightLeagues: GENERIC_PLACE_HALF_EXTENT.halfHeightLeagues,
    centreOffsetLeagues: berthOffsetLeagues(position, GENERIC_PLACE_HALF_EXTENT),
  };
}

export const CHARTED_PLACES: readonly PlaceSeed[] = [
  { id: HOME_PLACE_ID, label: HOME_PLACE_NAME, position: vec2(0, 0), extent: publishedExtent(HOME_PLACE_ID) ?? genericExtent(vec2(0, 0)) },
  ...CHARTED_LAYOUT_FRACTIONS.map((f, i) => seedAtSightDistance(i + 1, f * CHARTED_TRACK_LEAGUES)),
];

/**
 * WHICH AREA EACH PLACE IS WRITTEN INTO. A CLAIM, NOT A SOURCE.
 *
 * `regions.ts` `areaAt` derives the area from position and is the only thing the game reads.
 * This map exists so `areas.test.ts` can hold the WRITING in `gauntlet/island/REGIONS.md` to
 * that derivation: if an archetype moves, or a place moves, the two disagree and the suite goes
 * red rather than the dossier quietly describing the wrong climate.
 */
export const AREA_BY_PLACE: ReadonlyMap<string, SeaAreaId> = new Map([
  [HOME_PLACE_ID, HOME_AREA] as const,
  ...AUTHORED.map((a, i) => [`place-${pad2(i + 1)}`, a.area] as const),
]);
